import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, CalendarClock, Mail, Footprints, Link2 } from "lucide-react";
import { CTA } from "../data/quizData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reveal = ({ children, delay = 0, className }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Result = ({ result, submissionId, showEmailCapture, emailSent }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailState, setEmailState] = useState("idle"); // idle | sending | sent | error
  const [copied, setCopied] = useState(false);

  const bookingHref = submissionId ? `${CTA.url}?ref=${submissionId}` : CTA.url;

  const copyLink = async () => {
    const url = `${window.location.origin}/r/${submissionId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
      } catch {
        setCopied(false);
      }
      document.body.removeChild(textarea);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email || emailState === "sending") return;
    setEmailState("sending");
    try {
      await axios.patch(`${API}/submissions/${submissionId}/email`, {
        email,
        name: name || null,
      });
      setEmailState("sent");
    } catch {
      setEmailState("error");
    }
  };

  return (
    <div data-testid="result-screen" className="flex flex-col flex-1 pb-10">
      <Reveal>
        <span
          data-testid="result-badge"
          className="inline-block text-xs uppercase tracking-[0.2em] font-semibold text-[#B25E3B] bg-[#FBEBE4] rounded-full px-4 py-2 mb-5"
        >
          Your next step
        </span>
        <p
          data-testid="result-pattern-label"
          className="text-sm uppercase tracking-[0.15em] font-semibold text-[#6B8E7D] mb-4"
        >
          What's been stopping you: {result.label}
        </p>
        {emailSent && (
          <p
            data-testid="result-email-note"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#415A4F] bg-[#EDF3F0] rounded-full px-4 py-2 mb-6"
          >
            <Check className="w-4 h-4" /> A copy of this result is on its way to your inbox.
          </p>
        )}
        <p
          data-testid="result-opener"
          className="font-serif-display text-2xl sm:text-3xl leading-snug text-[#2C221E] mb-8"
        >
          {result.opener}
        </p>
      </Reveal>

      <Reveal delay={0.05} className="rounded-3xl bg-[#415A4F] text-[#FAF7F2] p-6 sm:p-8 mb-8">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#D9822B] mb-3">
          <Footprints className="w-4 h-4" /> Your first move — today
        </p>
        <p data-testid="result-step" className="font-serif-display text-xl sm:text-2xl leading-snug">
          {result.step}
        </p>
      </Reveal>

      <Reveal delay={0.05}>
        <p
          data-testid="result-bridge"
          className="text-base sm:text-lg text-[#5A4D46] leading-relaxed mb-10"
        >
          {result.bridge}
        </p>
      </Reveal>

      {showEmailCapture && (
        <Reveal delay={0.05} className="rounded-3xl bg-white border border-[#E9DFD6] p-6 sm:p-8 mb-8">
          <div data-testid="email-capture-card">
            {emailState === "sent" ? (
              <div data-testid="email-capture-success" className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EDF3F0]">
                  <Check className="w-4 h-4 text-[#415A4F]" />
                </span>
                <div>
                  <p className="font-semibold text-[#2C221E]">Done — it's on its way to you.</p>
                  <p className="text-sm text-[#85746C] mt-1">
                    Keep it somewhere you'll see it this week.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-3 mb-5">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FBEBE4]">
                    <Mail className="w-4 h-4 text-[#B25E3B]" />
                  </span>
                  <div>
                    <p className="font-semibold text-[#2C221E]">
                      Want this sent to your inbox so you don't lose it?
                    </p>
                    <p className="text-sm text-[#85746C] mt-1">
                      Completely optional. No spam, no pressure — just a copy of what you read here.
                    </p>
                  </div>
                </div>
                <form onSubmit={submitEmail} className="flex flex-col gap-3">
                  <input
                    data-testid="email-capture-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="First name (optional)"
                    className="w-full rounded-xl border border-[#E9DFD6] bg-[#FCF9F5] px-4 py-3.5 text-base text-[#2C221E] placeholder:text-[#85746C]/70 focus:outline-none focus:ring-2 focus:ring-[#B25E3B] focus:border-transparent"
                  />
                  <input
                    data-testid="email-capture-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    className="w-full rounded-xl border border-[#E9DFD6] bg-[#FCF9F5] px-4 py-3.5 text-base text-[#2C221E] placeholder:text-[#85746C]/70 focus:outline-none focus:ring-2 focus:ring-[#B25E3B] focus:border-transparent"
                  />
                  <button
                    data-testid="email-capture-submit-btn"
                    type="submit"
                    disabled={emailState === "sending"}
                    className="w-full rounded-full border-2 border-[#B25E3B] text-[#B25E3B] hover:bg-[#FBEBE4] font-semibold px-6 py-3.5 transition-colors duration-200 active:scale-[0.98] disabled:opacity-60"
                  >
                    {emailState === "sending" ? "Sending..." : "Send me my copy"}
                  </button>
                  {emailState === "error" && (
                    <p data-testid="email-capture-error" className="text-sm text-[#B25E3B]">
                      Something didn't go through — please try again.
                    </p>
                  )}
                </form>
              </>
            )}
          </div>
        </Reveal>
      )}

      {submissionId && (
        <Reveal delay={0.05} className="rounded-2xl border border-dashed border-[#B25E3B]/40 bg-[#FCF9F5] p-5 mb-8">
          <div data-testid="share-link-card" className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-[#5A4D46] text-center sm:text-left">
              Want to come back to this later? Keep your private link — no inbox digging.
            </p>
            <button
              data-testid="copy-share-link-btn"
              onClick={copyLink}
              className="inline-flex items-center gap-2 shrink-0 rounded-full border-2 border-[#B25E3B] text-[#B25E3B] hover:bg-[#FBEBE4] text-sm font-semibold px-5 py-2.5 transition-colors duration-200 active:scale-[0.98]"
            >
              {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
              {copied ? "Copied!" : "Copy my link"}
            </button>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.05} className="rounded-3xl bg-[#2C221E] text-[#FAF7F2] p-6 sm:p-8">
        <div id="booking-cta-card" data-testid="booking-cta-card">
          <span className="inline-block text-xs uppercase tracking-[0.2em] font-semibold text-[#D9822B] bg-[#D9822B]/15 rounded-full px-4 py-2 mb-4">
            Step 1 of 30 — done
          </span>
          <h3 className="font-serif-display text-2xl sm:text-3xl leading-snug mb-3">
            The rest of your roadmap is built live, with a human.
          </h3>
          <p data-testid="booking-cta-line" className="text-base text-[#FAF7F2]/70 leading-relaxed mb-6">
            {CTA.line}
          </p>
          <motion.a
            data-testid="book-kickstarter-btn"
            href={bookingHref}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#D9822B] hover:bg-[#c4741f] text-[#2C221E] text-lg font-bold px-8 py-5 transition-colors duration-200"
          >
            <CalendarClock className="w-5 h-5" />
            {CTA.buttonText}
          </motion.a>
          <p className="text-xs text-[#FAF7F2]/50 mt-4 text-center">
            One 90-minute session. A full 30-step roadmap. No obligation beyond that.
          </p>
        </div>
      </Reveal>
    </div>
  );
};
